import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createTransientSupabaseClient, supabase } from "../../lib/supabase";
import { V2_DEFAULT_SETTINGS } from "../lib/v2Schema";
import {
  clearV2Session,
  loadV2Session,
  loadV2Settings,
  saveV2Session,
  saveV2Settings,
} from "../lib/v2Storage";
import { ensureV2ReminderIdentity } from "../lib/v2ReminderRepository";
import { deactivateCurrentDevicePushSubscription, reconcilePushSubscription } from "../lib/v2PushNotifications";
import { migrateGuestCustomFoodsToAccount, migrateGuestFoodLogsToAccount } from "../lib/v2Database";
import {
  V2_REGISTRATION_STAGES,
  clearPendingRegistration,
  createPendingRegistrationRecord,
  getAuthProfile,
  getSafeAuthMessage,
  hasAnonymousAuthData,
  isAwaitingEmailVerification,
  isAnonymousUser,
  isPermanentUser,
  loadPendingRegistration,
  loadOrBootstrapV2Profile,
  loadV2AccessRole,
  needsPasswordSetup as userNeedsPasswordSetup,
  resolveV2SessionState,
  savePendingRegistration,
  transferAnonymousAuthData,
  upsertV2Profile,
  validateAnonymousRegistrationStart,
  verifyCompleteRegistrationCallback,
  validateVerifiedRegistrationCallback,
} from "../lib/v2AuthUtils";
import {
  hasAdminAccess as roleHasAdminAccess,
  isDeveloper as roleIsDeveloper,
  normalizeV2Role,
  V2_ROLES,
} from "../lib/v2Permissions";

export { V2_DEFAULT_SETTINGS };

const V2SessionContext = createContext(null);

function createCompatibilitySession(mode, user, profile = {}) {
  return {
    mode,
    created_at: new Date().toISOString(),
    user: {
      id: user?.id || "",
      email: user?.email || profile.email || "",
      full_name: profile.full_name || getAuthProfile(user).full_name || "",
      weight_kg: profile.weight_kg ?? getAuthProfile(user).weight_kg ?? "",
      guest: mode === "guest",
    },
  };
}

async function deactivateAnonymousServerData(userId) {
  if (!userId) return { ok: true, error: "" };

  const now = new Date().toISOString();
  const updates = await Promise.all([
    supabase
      .from("v2_medication_reminders")
      .update({ active: false, next_run_at: null, updated_at: now })
      .eq("user_id", userId)
      .eq("active", true),
    supabase
      .from("v2_notification_settings")
      .update({
        whatsapp_reminders_enabled: false,
        push_notifications_enabled: false,
        daily_system_reminder_enabled: false,
        daily_system_next_run_at: null,
        updated_at: now,
      })
      .eq("user_id", userId),
    deactivateCurrentDevicePushSubscription({ userId }),
  ]);

  const failed = updates.find((result) => result?.error || result?.ok === false);
  if (failed) return { ok: false, error: failed.error || "Guest reminders could not be paused." };
  return { ok: true, error: "" };
}

export function V2SessionProvider({ children }) {
  const [localSession, setLocalSession] = useState(() => loadV2Session());
  const [settings, setSettings] = useState(() => loadV2Settings());
  const [authSession, setAuthSession] = useState(null);
  const [authEvent, setAuthEvent] = useState("");
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [logoutWarning, setLogoutWarning] = useState("");
  const [pendingHandoff, setPendingHandoff] = useState(null);
  const [verifiedRegistration, setVerifiedRegistration] = useState(null);
  const [role, setRole] = useState(V2_ROLES.user);

  const user = authSession?.user || null;
  const sessionState = resolveV2SessionState({
    localSession,
    user,
    settings,
    verifiedRegistration,
  });
  const isGuest = sessionState.isGuest;
  const needsPasswordSetup = sessionState.needsPasswordSetup;
  const isRegistered = sessionState.isRegistered;
  const hasV2Session = sessionState.hasV2Session;
  const hasRequiredWeight = sessionState.hasRequiredWeight;
  const isPasswordRecovery = authEvent === "PASSWORD_RECOVERY";
  const normalizedRole = normalizeV2Role(role);
  const hasAdminAccess = isRegistered && roleHasAdminAccess(normalizedRole);
  const isDeveloper = isRegistered && roleIsDeveloper(normalizedRole);

  const saveSession = (nextSession) => {
    setLocalSession(nextSession);
    if (nextSession) saveV2Session(nextSession);
    else clearV2Session();
  };

  const saveSettings = (nextSettings) => {
    const merged = saveV2Settings(nextSettings);
    setSettings(merged);

    setLocalSession((current) => {
      if (!current) return current;
      const updated = {
        ...current,
        user: {
          ...current.user,
          full_name: merged.profile.full_name,
          weight_kg: merged.profile.weight_kg,
        },
      };
      saveV2Session(updated);
      return updated;
    });

    return merged;
  };

  const hydratePermanentUser = async (authUser) => {
    if (!isPermanentUser(authUser) || userNeedsPasswordSetup(authUser) || isAwaitingEmailVerification(authUser)) {
      setRole(V2_ROLES.user);
      setLoading(false);
      return;
    }

    setLoading(true);
    const [profileResult, roleResult] = await Promise.all([
      loadOrBootstrapV2Profile(authUser),
      loadV2AccessRole(authUser),
    ]);
    setRole(normalizeV2Role(roleResult.role));

    if (!profileResult.ok) {
      setProfileError(profileResult.error || "Profile loading error.");
    } else {
      setProfileError("");
      const refreshedSettings = loadV2Settings();
      setSettings(refreshedSettings);
      saveSession(createCompatibilitySession("registered", authUser, refreshedSettings.profile));
      reconcilePushSubscription().catch(() => {});
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setAuthSession(data?.session || null);
      if (data?.session?.user) {
        setRole(V2_ROLES.user);
        setLoading(true);
        window.setTimeout(() => hydratePermanentUser(data.session.user), 0);
      } else {
        setRole(V2_ROLES.user);
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!alive) return;
      setAuthSession(session || null);
      setAuthEvent(event);

      if (event === "SIGNED_OUT") {
        setPendingHandoff(null);
        setVerifiedRegistration(null);
        setRole(V2_ROLES.user);
        setLoading(false);
        return;
      }

      if (session?.user) {
        setRole(V2_ROLES.user);
        setLoading(true);
        window.setTimeout(() => hydratePermanentUser(session.user), 0);
      } else {
        setRole(V2_ROLES.user);
        setLoading(false);
      }
    });

    return () => {
      alive = false;
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterGuest = async (profile = {}) => {
    setRole(V2_ROLES.user);
    const identity = await ensureV2ReminderIdentity();
    if (!identity.ok) return identity;

    const nextSession = createCompatibilitySession("guest", identity.user, {
      full_name: profile.full_name || "Guest",
      weight_kg: profile.weight_kg,
    });

    saveSession(nextSession);
    saveSettings({
      ...settings,
      profile: {
        ...settings.profile,
        full_name: profile.full_name || settings.profile.full_name || "Guest",
        weight_kg: profile.weight_kg || settings.profile.weight_kg,
      },
    });
    setAuthSession((await supabase.auth.getSession()).data?.session || null);
    return { ok: true, user: identity.user };
  };

  const startRegistration = async ({ full_name, email, weight_kg }) => {
    const identity = await ensureV2ReminderIdentity();
    if (!identity.ok) return identity;
    if (!isAnonymousUser(identity.user)) {
      return { ok: false, error: "Registration must start from a guest identity." };
    }

    const anonymousSession = (await supabase.auth.getSession()).data?.session;
    if (!anonymousSession?.access_token || anonymousSession.user?.id !== identity.user.id) {
      return { ok: false, error: "Anonymous registration session is unavailable." };
    }

    const normalizedEmail = String(email || "").trim().toLowerCase();
    const profile = {
      full_name: String(full_name || "").trim() || "Patient",
      weight_kg,
    };

    const result = await supabase.auth.updateUser(
      {
        email: normalizedEmail,
        data: {
          full_name: profile.full_name,
          weight_kg: profile.weight_kg,
          registration_stage: V2_REGISTRATION_STAGES.awaitingEmailVerification,
        },
      },
      {
        emailRedirectTo: `${window.location.origin}/complete-registration`,
      }
    );

    if (result.error) {
      return { ok: false, error: getSafeAuthMessage(result.error, "Registration could not be started.") };
    }

    const pendingValidation = validateAnonymousRegistrationStart(result.data, identity.user.id);
    if (!pendingValidation.ok) {
      return pendingValidation;
    }

    const pendingRecord = createPendingRegistrationRecord({
      anonymousUser: identity.user,
      email: normalizedEmail,
      ...profile,
    });
    savePendingRegistration(pendingRecord);
    saveSettings({
      ...settings,
      profile: {
        ...settings.profile,
        ...profile,
      },
    });
    saveSession(createCompatibilitySession("awaiting_email_verification", identity.user, { ...profile, email: normalizedEmail }));
    setAuthSession((await supabase.auth.getSession()).data?.session || null);

    return {
      ok: true,
      userId: identity.user.id,
      emailVerified: false,
    };
  };

  const beginCompleteRegistration = async (callback = {}) => {
    const callbackParams = typeof callback === "string" ? { code: callback } : callback;
    const pending = loadPendingRegistration();
    const verification = await verifyCompleteRegistrationCallback(callbackParams);
    if (!verification.ok) {
      return { ok: false, error: verification.error || "Invalid or expired link." };
    }

    const verifiedSession = verification.session;
    const callbackValidation = validateVerifiedRegistrationCallback({
      verifiedSession,
      pending,
    });
    if (!callbackValidation.ok) {
      return callbackValidation;
    }

    const verifiedUser = callbackValidation.user;
    const pendingMatchesUser = pending?.anonymous_user_id === verifiedUser.id;
    const authProfile = getAuthProfile(verifiedUser);
    const profile = {
      full_name: pendingMatchesUser ? pending.full_name || authProfile.full_name : authProfile.full_name,
      weight_kg: pendingMatchesUser ? pending.weight_kg ?? authProfile.weight_kg : authProfile.weight_kg,
    };

    const stageResult = await supabase.auth.updateUser({
      data: {
        full_name: profile.full_name || "Patient",
        weight_kg: profile.weight_kg,
        registration_stage: V2_REGISTRATION_STAGES.setPassword,
      },
    });
    if (stageResult.error) {
      return { ok: false, error: getSafeAuthMessage(stageResult.error, "Could not prepare password setup.") };
    }

    const updatedUser = stageResult.data?.user || verifiedUser;
    const passwordSetupSession = {
      ...verifiedSession,
      user: updatedUser,
    };

    setVerifiedRegistration({ permanentSession: passwordSetupSession, pending: pendingMatchesUser ? pending : null });
    setAuthSession(passwordSetupSession);
    saveSettings({
      ...settings,
      profile: {
        ...settings.profile,
        full_name: profile.full_name || settings.profile.full_name,
        weight_kg: profile.weight_kg ?? settings.profile.weight_kg,
      },
    });
    saveSession(createCompatibilitySession("awaiting_password_setup", updatedUser, profile));
    return { ok: true, user: updatedUser };
  };

  const completeRegistration = async ({ password }) => {
    const current = (await supabase.auth.getSession()).data?.session;
    if (!current?.access_token || !current?.refresh_token || !current?.user?.id) {
      return { ok: false, error: "Invalid or expired link." };
    }

    const verifiedUser =
      verifiedRegistration?.permanentSession?.user?.id === current.user.id ? verifiedRegistration.permanentSession.user : null;
    const authUser = userNeedsPasswordSetup(current.user) ? current.user : verifiedUser || current.user;
    if (!isPermanentUser(authUser) || !userNeedsPasswordSetup(authUser)) {
      return { ok: false, error: "Invalid or expired link." };
    }

    const currentProfile = {
      ...getAuthProfile(authUser),
      full_name: verifiedRegistration?.pending?.full_name || getAuthProfile(authUser).full_name,
      weight_kg: verifiedRegistration?.pending?.weight_kg ?? getAuthProfile(authUser).weight_kg,
    };
    const result = await supabase.auth.updateUser({
      password,
      data: {
        registration_stage: V2_REGISTRATION_STAGES.complete,
        full_name: currentProfile.full_name,
        weight_kg: currentProfile.weight_kg,
      },
    });
    if (result.error) {
      return { ok: false, error: getSafeAuthMessage(result.error, "Password setup failed.") };
    }

    const profileResult = await upsertV2Profile(currentProfile, { user: result.data.user });
    if (!profileResult.ok) return profileResult;

    const customFoodMigration = await migrateGuestCustomFoodsToAccount(result.data.user.id).catch(() => ({
      ok: false,
      error: "Guest custom foods could not be saved to this account.",
    }));
    const foodLogMigration = await migrateGuestFoodLogsToAccount(result.data.user.id).catch(() => ({
      ok: false,
      error: "Guest food logs could not be saved to this account.",
    }));
    const refreshedSettings = loadV2Settings();
    setSettings(refreshedSettings);
    saveSession(createCompatibilitySession("registered", result.data.user, refreshedSettings.profile));
    clearPendingRegistration();
    setVerifiedRegistration(null);
    await reconcilePushSubscription().catch(() => null);
    return { ok: true, user: result.data.user, profile: profileResult.profile, customFoodMigration, foodLogMigration };
  };

  const login = async ({ email, password }) => {
    setPendingHandoff(null);
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const existingSession = (await supabase.auth.getSession()).data?.session;

    if (existingSession?.user && isAnonymousUser(existingSession.user)) {
      const dataCheck = await hasAnonymousAuthData(existingSession.user.id);
      if (!dataCheck.ok) return dataCheck;

      if (dataCheck.hasData) {
        const tempClient = createTransientSupabaseClient();
        const result = await tempClient.auth.signInWithPassword({ email: normalizedEmail, password });
        if (result.error) {
          return { ok: false, error: getSafeAuthMessage(result.error, "Invalid email or password.") };
        }
        if (!result.data?.session?.access_token || !result.data?.session?.refresh_token) {
          return { ok: false, error: "Could not prepare secure account handoff." };
        }
        setPendingHandoff({
          anonymousSession: existingSession,
          permanentSession: result.data.session,
        });
        return { ok: false, requiresHandoff: true };
      }
    }

    const result = await supabase.auth.signInWithPassword({ email: normalizedEmail, password });
    if (result.error) {
      return { ok: false, error: getSafeAuthMessage(result.error, "Invalid email or password.") };
    }

    await hydratePermanentUser(result.data.user);
    return { ok: true, user: result.data.user };
  };

  const completeLoginHandoff = async (mode) => {
    if (!pendingHandoff) return { ok: false, error: "Anonymous reminder handoff choice is required." };

    const anonymousUserId = pendingHandoff.anonymousSession.user.id;
    if (mode === "pause") {
      const unsubscribeResult = await deactivateCurrentDevicePushSubscription({ userId: anonymousUserId });
      if (!unsubscribeResult.ok) return unsubscribeResult;
    }

    const handoff = await transferAnonymousAuthData({
      anonymousAccessToken: pendingHandoff.anonymousSession.access_token,
      permanentAccessToken: pendingHandoff.permanentSession.access_token,
      mode,
    });
    if (!handoff.ok) return handoff;

    const setSessionResult = await supabase.auth.setSession({
      access_token: pendingHandoff.permanentSession.access_token,
      refresh_token: pendingHandoff.permanentSession.refresh_token,
    });
    if (setSessionResult.error) {
      return { ok: false, error: "Could not restore the signed-in session." };
    }

    setPendingHandoff(null);
    await hydratePermanentUser(setSessionResult.data.session.user);
    const foodLogMigration =
      mode === "move"
        ? await migrateGuestFoodLogsToAccount(setSessionResult.data.session.user.id).catch(() => ({
            ok: false,
            error: "Guest food logs could not be saved to this account.",
          }))
        : { ok: true, count: 0, error: "" };
    return { ok: true, user: setSessionResult.data.session.user, foodLogMigration };
  };

  const logout = async () => {
    setLogoutWarning("");
    const currentUser = user;

    if (isPermanentUser(currentUser)) {
      const pushResult = await deactivateCurrentDevicePushSubscription({ userId: currentUser.id });
      if (!pushResult.ok) {
        const warning = pushResult.error || "Secure logout warning: this device is still connected to Push.";
        setLogoutWarning(warning);
        return { ok: false, warning };
      }
    }

    const result = await supabase.auth.signOut();
    if (result.error) return { ok: false, error: getSafeAuthMessage(result.error, "Logout failed.") };
    saveSession(null);
    setRole(V2_ROLES.user);
    return { ok: true };
  };

  const exitGuestMode = async () => {
    if (!isGuest || !user?.id) {
      saveSession(null);
      setRole(V2_ROLES.user);
      return { ok: true };
    }

    const pauseResult = await deactivateAnonymousServerData(user.id);
    if (!pauseResult.ok) return pauseResult;

    const signOut = await supabase.auth.signOut();
    if (signOut.error) return { ok: false, error: "Guest sign-out failed." };
    saveSession(null);
    setRole(V2_ROLES.user);
    return { ok: true };
  };

  const syncProfile = async (profile) => {
    if (!isRegistered || !user) return { ok: true, profile: null };
    return upsertV2Profile(profile, { user });
  };

  const value = useMemo(
    () => ({
      session: localSession,
      authSession,
      user,
      settings,
      loading,
      profileError,
      logoutWarning,
      pendingHandoff,
      verifiedRegistration,
      hasV2Session,
      hasRequiredWeight,
      isGuest,
      isRegistered,
      role: normalizedRole,
      hasAdminAccess,
      isDeveloper,
      needsPasswordSetup,
      isPasswordRecovery,
      enterGuest,
      startRegistration,
      beginCompleteRegistration,
      completeRegistration,
      login,
      completeLoginHandoff,
      logout,
      exitGuestMode,
      saveSettings,
      syncProfile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      localSession,
      authSession,
      user,
      settings,
      loading,
      profileError,
      logoutWarning,
      pendingHandoff,
      verifiedRegistration,
      hasV2Session,
      hasRequiredWeight,
      isGuest,
      isRegistered,
      normalizedRole,
      hasAdminAccess,
      isDeveloper,
      needsPasswordSetup,
      isPasswordRecovery,
    ]
  );

  return <V2SessionContext.Provider value={value}>{children}</V2SessionContext.Provider>;
}

export function useV2Session() {
  return useContext(V2SessionContext);
}
